import {
  ArrowRight,
  Briefcase,
  CalendarClock,
  Check,
  Euro,
  Flag,
  ListChecks,
  ListTodo,
  Phone,
  Plus,
  Users,
  Video,
} from "lucide-react";
import { useTranslate } from "ra-core";
import { Link } from "react-router";

import { cn } from "@/lib/utils";

import type { AttentionGroupKey, AttentionItem } from "./homeModel";
import { attentionHref, canMarkDone } from "./homeUi";
import type { AttentionLookup } from "./useAttentionLookup";
import { useHomeFormat, type HomeFormat } from "./useHomeFormat";

type Tone = "danger" | "info" | "warning" | "neutral";

const GROUP_TONE: Record<AttentionGroupKey, Tone> = {
  overdue: "danger",
  today: "info",
  no_next_step: "warning",
  collect: "neutral",
};

const iconOf = (item: AttentionItem) => {
  switch (item.source) {
    case "task":
      if (item.taskKind === "internal") return Users;
      return item.taskType === "call" ? Phone : ListTodo;
    case "team_task":
      return ListChecks;
    case "milestone":
      return Flag;
    case "event":
      return item.eventKind === "call"
        ? Phone
        : item.eventKind === "demo" || item.eventKind === "meeting"
          ? Video
          : CalendarClock;
    case "deal":
      return Briefcase;
    case "invoice":
      return Euro;
  }
};

/** Secondary line: company / contact · kind · money or idle time. */
const useSubtitle = (lookup: AttentionLookup, format: HomeFormat) => {
  const translate = useTranslate();
  const t = (key: string, options?: Record<string, unknown>) =>
    translate(`desk.home.inbox.${key}`, options);
  return (item: AttentionItem, mobile: boolean): string => {
    const parts: (string | null | undefined)[] = [];
    switch (item.source) {
      case "task":
        parts.push(
          lookup.contactName(item.contactId),
          t(item.taskKind === "internal" ? "kind_internal" : "kind_client"),
          mobile ? null : lookup.taskTypeLabel(item.taskType),
        );
        break;
      case "team_task":
        parts.push(
          lookup.dealName(item.dealId),
          t("team_task"),
          item.priority === "high" || item.priority === "urgent"
            ? t(`priority_${item.priority}`)
            : null,
        );
        break;
      case "milestone":
        parts.push(
          lookup.dealName(item.dealId),
          t("milestone"),
          item.amount ? format.money(item.amount) : null,
        );
        break;
      case "event":
        parts.push(
          lookup.dealName(item.dealId) ?? lookup.itemCompany(item),
          t(`event_kinds.${item.eventKind ?? "other"}`),
        );
        break;
      case "deal":
        parts.push(
          mobile ? null : lookup.stageLabel(item.stage),
          format.money(item.amount ?? 0),
          item.closingPassed && item.date
            ? t("closing_passed", { date: format.shortDate(item.date) })
            : t("idle", { smart_count: item.idleDays ?? 0 }),
        );
        break;
      case "invoice":
        parts.push(
          lookup.itemCompany(item),
          format.money(item.amount ?? 0),
          item.date
            ? item.daysLate > 0
              ? t("invoice_overdue", { smart_count: item.daysLate })
              : t("invoice_due", { date: format.shortDate(item.date) })
            : null,
        );
        break;
    }
    return parts.filter(Boolean).join(" · ");
  };
};

/** Short urgency chip: "hace 3 días", "10:30", "18 d"… */
const useChip = (format: HomeFormat) => {
  const translate = useTranslate();
  const t = (key: string, options?: Record<string, unknown>) =>
    translate(`desk.home.inbox.chip.${key}`, options);
  return (item: AttentionItem, group: AttentionGroupKey): string | null => {
    if (group === "overdue")
      return item.daysLate === 1
        ? t("yesterday")
        : t("late", { smart_count: item.daysLate });
    if (group === "today")
      return item.timed && item.date ? format.time(item.date) : t("today");
    if (group === "no_next_step")
      return item.closingPassed
        ? t("closing_late", { smart_count: item.daysLate })
        : t("idle_days", { smart_count: item.idleDays ?? 0 });
    return item.daysLate > 0
      ? t("invoice_late", { smart_count: item.daysLate })
      : item.date
        ? format.shortDate(item.date)
        : null;
  };
};

export interface AttentionRowProps {
  item: AttentionItem;
  group: AttentionGroupKey;
  lookup: AttentionLookup;
  variant: "desk" | "mobile";
  onDone: (item: AttentionItem) => void;
  onNextStep: (item: AttentionItem) => void;
}

export const AttentionRow = ({
  item,
  group,
  lookup,
  variant,
  onDone,
  onNextStep,
}: AttentionRowProps) => {
  const translate = useTranslate();
  const format = useHomeFormat();
  const subtitle = useSubtitle(lookup, format);
  const chip = useChip(format);
  const mobile = variant === "mobile";
  const Icon = iconOf(item);
  const href = attentionHref(item, mobile);
  const tone = GROUP_TONE[group];
  const chipLabel = chip(item, group);
  const t = (key: string, options?: Record<string, unknown>) =>
    translate(`desk.home.inbox.actions.${key}`, options);

  const action = canMarkDone(item) ? (
    mobile ? (
      <button
        type="button"
        className="home-row__check"
        aria-label={t("done_label", { title: item.title })}
        onClick={() => onDone(item)}
      >
        <Check aria-hidden className="size-4" strokeWidth={2} />
      </button>
    ) : (
      <button
        type="button"
        className="home-row__btn desk-press"
        aria-label={t("done_label", { title: item.title })}
        onClick={() => onDone(item)}
      >
        <Check aria-hidden className="size-3.5" strokeWidth={2} />
        {t("done")}
      </button>
    )
  ) : item.source === "deal" ? (
    <>
      {item.closingPassed && !mobile ? (
        <Link
          to={`/deals/${item.id}`}
          className="home-row__btn home-row__btn--ghost desk-press"
          aria-label={`${t("move_date")}: ${item.title}`}
        >
          {t("move_date")}
        </Link>
      ) : null}
      <button
        type="button"
        className={cn(
          "home-row__btn desk-press",
          mobile ? "home-row__btn--soft" : "home-row__btn--primary",
        )}
        aria-label={t("next_step_label", { title: item.title })}
        onClick={() => onNextStep(item)}
      >
        {mobile ? null : (
          <Plus aria-hidden className="size-3.5" strokeWidth={2} />
        )}
        {mobile ? t("next_step_short") : t("next_step")}
      </button>
    </>
  ) : (
    <Link
      to={href}
      className="home-row__btn desk-press"
      aria-label={`${t(item.source === "invoice" ? "open_invoice" : "open")}: ${item.title}`}
    >
      {t(item.source === "invoice" ? "open_invoice" : "open")}
      {item.source === "event" ? (
        <ArrowRight aria-hidden className="size-3.5" strokeWidth={2} />
      ) : null}
    </Link>
  );

  return (
    <li className="home-row" data-variant={variant}>
      <span className="home-row__icon" data-tone={tone} aria-hidden>
        <Icon className="size-4" strokeWidth={1.75} />
      </span>
      <span className="home-row__text">
        <Link to={href} className="home-row__title">
          {item.title}
        </Link>
        <span className="home-row__subtitle">
          {mobile && chipLabel && (group === "overdue" || group === "today")
            ? [chipLabel, subtitle(item, mobile)].filter(Boolean).join(" · ")
            : subtitle(item, mobile)}
        </span>
      </span>
      {chipLabel && !mobile ? (
        <span className="home-row__chip" data-tone={tone}>
          {chipLabel}
        </span>
      ) : null}
      <span className="home-row__action">{action}</span>
    </li>
  );
};
